import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { Components } from 'react-markdown';
import {
  Summarize as SummaryIcon,
  Build as OperationsIcon,
  NewReleases as VersionIcon,
  FolderOpen as FilesIcon,
  Science as TechnicalIcon,
  AutoAwesome as ChangedIcon,
  AddCircleOutline as AddedIcon,
  LabelOutlined as SectionIcon,
} from '@mui/icons-material';

function markdownChildrenToText(children: React.ReactNode): string {
  if (children == null || typeof children === 'boolean') return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(markdownChildrenToText).join('');
  if (React.isValidElement(children)) {
    return markdownChildrenToText(children.props.children);
  }
  return '';
}

function normalizeSectionTitle(title: string): string {
  return title.replace(/[^\w\s]/g, '').trim().toLowerCase();
}

function sectionAccent(title: string): { Icon: typeof SectionIcon; color: string } {
  const key = normalizeSectionTitle(title);
  if (key.includes('summary')) return { Icon: SummaryIcon, color: '#3b9eff' };
  if (key.includes('operations')) return { Icon: OperationsIcon, color: '#d97706' };
  if (key.includes('version')) return { Icon: VersionIcon, color: '#0d1f3c' };
  if (key.includes('files')) return { Icon: FilesIcon, color: '#0284c7' };
  if (key.includes('technical')) return { Icon: TechnicalIcon, color: '#6366f1' };
  if (key.includes('changed')) return { Icon: ChangedIcon, color: '#7c3aed' };
  if (key.includes('added')) return { Icon: AddedIcon, color: '#1a4d3a' };
  return { Icon: SectionIcon, color: '#64748b' };
}

export function useChangelogMarkdownComponents(): Components {
  const theme = useTheme();

  return {
    h3: ({ children }) => {
      const title = markdownChildrenToText(children);
      const { Icon } = sectionAccent(title);

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 2.5,
            mb: 1,
            py: 0.5,
          }}
        >
          <Icon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography
            component="span"
            variant="caption"
            sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.secondary' }}
          >
            {title}
          </Typography>
        </Box>
      );
    },
    h4: ({ children }) => (
      <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 0.75, color: 'text.primary' }}>
        {children}
      </Typography>
    ),
    p: ({ children }) => (
      <Typography variant="body2" sx={{ mb: 1.25, lineHeight: 1.65, color: 'text.primary' }}>
        {children}
      </Typography>
    ),
    ul: ({ children }) => (
      <Box
        component="ul"
        sx={{
          pl: 0,
          mb: 1.5,
          mt: 0,
          listStyle: 'none',
          '& > li': {
            position: 'relative',
            pl: 2,
            mb: 0.75,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '0.55em',
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: 'secondary.main',
            },
          },
        }}
      >
        {children}
      </Box>
    ),
    ol: ({ children }) => (
      <Box component="ol" sx={{ pl: 2.5, mb: 1.5, mt: 0, '& > li': { mb: 0.75 } }}>
        {children}
      </Box>
    ),
    li: ({ children }) => (
      <Typography component="li" variant="body2" sx={{ lineHeight: 1.65 }}>
        {children}
      </Typography>
    ),
    strong: ({ children }) => (
      <Box component="strong" sx={{ fontWeight: 700, color: 'primary.main' }}>
        {children}
      </Box>
    ),
    a: ({ href, children }) => (
      <Box
        component="a"
        href={href}
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
        sx={{
          color: 'secondary.main',
          fontWeight: 600,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {children}
      </Box>
    ),
    code: ({ children }) => (
      <Box
        component="code"
        sx={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.8rem',
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          color: 'primary.main',
          px: 0.6,
          py: 0.15,
          borderRadius: 0.75,
          border: 1,
          borderColor: alpha(theme.palette.primary.main, 0.12),
        }}
      >
        {children}
      </Box>
    ),
    table: ({ children }) => (
      <Box
        sx={{
          overflowX: 'auto',
          my: 1.5,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.8125rem',
            '& th': {
              textAlign: 'left',
              fontWeight: 700,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              color: 'primary.main',
              px: 1.5,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
            },
            '& td': {
              px: 1.5,
              py: 0.85,
              borderBottom: 1,
              borderColor: alpha(theme.palette.divider, 0.7),
              verticalAlign: 'top',
            },
            '& tr:last-of-type td': { borderBottom: 'none' },
          }}
        >
          {children}
        </Box>
      </Box>
    ),
    hr: () => (
      <Box
        sx={{
          my: 2,
          height: 1,
          border: 0,
          bgcolor: alpha(theme.palette.divider, 0.8),
        }}
      />
    ),
  };
}
